import { useCallback, useMemo, useState } from 'react'
import { Box, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { AlertTriangleIcon, ClapperboardIcon, KanbanIcon, ListIcon } from 'lucide-react'
import { Alert, Button } from '@repo/ui'
import {
  VERAFIED_EPISODE_STATUS_LABELS,
  VERAFIED_EVALUATION_LABELS,
  type VerafiedEpisodeStatus,
} from '@repo/types'
import { Search } from 'components/input/Search'

import { EmptyState, SelectField, Table } from 'components'
import { KanbanBoard, KanbanBoardSkeleton } from 'components/display/KanbanBoard'
import { ViewToggle } from 'components/input/ViewToggle/ViewToggle'
import { useSnackbar } from 'contexts'
import { useVerafiedActions, useVerafiedEpisodes, useVerafiedGuests } from 'hooks'
import type { VerafiedEpisode } from 'services'

import { ToneChip } from '../../InternalAnalyticsPage/components/shared'
import { EpisodeFormModal } from './EpisodeFormModal'
import { SuggestionsReviewModal } from './SuggestionsReviewModal'
import {
  EMPTY_EPISODE_FILTERS,
  EPISODE_BOARD_LANES,
  EPISODE_STATUS_OPTIONS,
  EVALUATION_OPTIONS,
  EVALUATION_TONE,
  filterEpisodes,
  INDUSTRY_OPTIONS,
  industryLabel,
  lengthLabel,
  SOLUTION_OPTIONS,
  solutionLabel,
  UNRATED,
  episodeMoveVerdict,
  type EpisodeFilters,
} from './verafiedModel'

const LAYOUTS = [
  { value: 'table' as const, label: 'Table', icon: ListIcon },
  { value: 'board' as const, label: 'Board', icon: KanbanIcon },
]

const asList = (v: string | string[]) => (Array.isArray(v) ? v : v ? [v] : [])

const formatDate = (d: string | null) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''

const EpisodeCard = ({ episode, onOpen }: { episode: VerafiedEpisode; onOpen: (e: VerafiedEpisode) => void }) => {
  const theme = useTheme()
  const guests = episode.guests.map((g) => g.name).join(', ')
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(episode)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(episode)
      }}
      data-testid={`episode-card-${episode.id}`}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: 'background.paper',
        cursor: 'pointer',
        '&:hover, &:focus-visible': {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
          outline: 'none',
        },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="body3" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
          {episode.title}
        </Typography>
        {(episode.evaluation || episode.claims_check) && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {episode.evaluation && <ToneChip tone={EVALUATION_TONE[episode.evaluation]} label={VERAFIED_EVALUATION_LABELS[episode.evaluation]} />}
            {episode.claims_check && (
              <Tooltip title={episode.claims_check}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', color: theme.palette.warning.main }}>
                  <AlertTriangleIcon size={14} strokeWidth={1.5} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        )}
        {guests && (
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {guests}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}

/**
 * VRG-734 — VERAfied episodes: a table, and a board by status on the shared KanbanBoard (the deals
 * board's mechanics, extracted on this second use). Evaluation (the editorial verdict) and status
 * (the pipeline) are separate fields, as the spec asks.
 */
export const EpisodesView = ({ canManage, adding, onAddingDone }: { canManage: boolean; adding: boolean; onAddingDone: () => void }) => {
  const { open: notify } = useSnackbar()
  const { data, isLoading, error } = useVerafiedEpisodes()
  const guestsQuery = useVerafiedGuests()
  const { updateEpisode } = useVerafiedActions()
  const [filters, setFilters] = useState<EpisodeFilters>(EMPTY_EPISODE_FILTERS)
  const [layout, setLayout] = useState<'table' | 'board'>('table')
  const [showShelved, setShowShelved] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [reviewing, setReviewing] = useState(false)
  const closeReview = useCallback(() => setReviewing(false), [])

  const episodes = useMemo(() => data ?? [], [data])
  const rows = useMemo(() => filterEpisodes(episodes, filters), [episodes, filters])
  // Re-read from the query so the modal reflects a write made inside it (a guest linked, say).
  const editing = editingId ? episodes.find((e) => e.id === editingId) ?? null : null
  const guestOptions = useMemo(
    () => (guestsQuery.data ?? []).filter((g) => g.episodes.length).map((g) => ({ value: g.id, label: g.name })),
    [guestsQuery.data],
  )
  const set = <K extends keyof EpisodeFilters>(k: K, v: EpisodeFilters[K]) => setFilters((f) => ({ ...f, [k]: v }))
  const filtered = rows.length !== episodes.length

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Episode',
        type: 'string',
        flex: 2,
        minWidth: 280,
        renderCell: ({ row }: { row: VerafiedEpisode }) => (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Link component="button" variant="body3" underline="hover" sx={{ fontWeight: 500, textAlign: 'left' }} onClick={() => setEditingId(row.id)} noWrap>
              {row.title}
            </Link>
            {row.claims_check && (
              <Tooltip title={row.claims_check}>
                <Box sx={{ display: 'inline-flex', color: 'warning.main', flexShrink: 0 }}>
                  <AlertTriangleIcon size={14} strokeWidth={1.5} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'evaluation',
        headerName: 'Evaluation',
        type: 'string',
        width: 140,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => (row.evaluation ? VERAFIED_EVALUATION_LABELS[row.evaluation] : ''),
        renderCell: ({ row }: { row: VerafiedEpisode }) =>
          row.evaluation ? <ToneChip tone={EVALUATION_TONE[row.evaluation]} label={VERAFIED_EVALUATION_LABELS[row.evaluation]} /> : null,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 160,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => VERAFIED_EPISODE_STATUS_LABELS[row.status],
      },
      {
        field: 'guests',
        headerName: 'Guests',
        type: 'string',
        flex: 1.2,
        minWidth: 180,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => row.guests.map((g) => g.name).join(', '),
      },
      {
        field: 'solutions',
        headerName: 'Solutions',
        type: 'string',
        flex: 1,
        minWidth: 170,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => row.solutions.map(solutionLabel).join(', '),
      },
      {
        field: 'industries',
        headerName: 'Industries',
        type: 'string',
        flex: 1,
        minWidth: 160,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => row.industries.map(industryLabel).join(', '),
      },
      {
        field: 'recording_on',
        headerName: 'Recording',
        type: 'string',
        width: 130,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => row.recording_on ?? '',
        renderCell: ({ row }: { row: VerafiedEpisode }) => formatDate(row.recording_on),
      },
      {
        field: 'publish_on',
        headerName: 'Publish',
        type: 'string',
        width: 130,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => row.publish_on ?? '',
        renderCell: ({ row }: { row: VerafiedEpisode }) => formatDate(row.publish_on),
      },
      {
        field: 'length_seconds',
        headerName: 'Length',
        type: 'string',
        width: 100,
        valueGetter: (_v: unknown, row: VerafiedEpisode) => lengthLabel(row.length_seconds) ?? '',
      },
    ],
    [],
  )

  const move = (e: VerafiedEpisode, _from: VerafiedEpisodeStatus, to: VerafiedEpisodeStatus) =>
    updateEpisode.mutate(
      { id: e.id, input: { status: to } },
      // No success toast: the card visibly lands in its lane (toast policy — visible success is silent).
      { onError: (err) => notify({ type: 'error', text: (err as Error).message }) },
    )

  const suggested = episodes.filter((e) => e.suggestion).length
  const countAndToggle = (
    <Stack direction="row" alignItems="center" spacing={3}>
      <ViewToggle options={LAYOUTS} value={layout} onChange={setLayout} ariaLabel="Episode layout" />
      <Typography variant="body3" color="text.secondary">
        {isLoading ? ' ' : filtered ? `${rows.length} of ${episodes.length} episodes` : `${episodes.length} episodes`}
      </Typography>
    </Stack>
  )

  return (
    <Stack spacing={4}>
      {canManage && suggested > 0 && (
        <Alert
          severity="info"
          description={`${suggested} ${suggested === 1 ? 'episode has' : 'episodes have'} suggested solutions and industries waiting for review.`}
          button={
            <Button variant="secondary" onClick={() => setReviewing(true)}>
              Review
            </Button>
          }
        />
      )}
      <Stack direction="row" spacing={3} useFlexGap alignItems="flex-end" sx={{ flexWrap: 'wrap', rowGap: 3 }}>
        <Box sx={{ width: 300 }}>
          <Search value={filters.q} onChange={(v: string) => set('q', v)} onClear={() => set('q', '')} placeholder="Search episodes" fullWidth />
        </Box>
        <Box sx={{ width: 140 }}>
          <SelectField
            multiple
            placeholder="Evaluation"
            options={[...EVALUATION_OPTIONS, { value: UNRATED, label: 'Unrated' }]}
            value={filters.evaluation}
            onChange={(v) => set('evaluation', asList(v))}
          />
        </Box>
        {layout === 'table' && (
          <Box sx={{ width: 150 }}>
            <SelectField multiple placeholder="Status" options={EPISODE_STATUS_OPTIONS} value={filters.status} onChange={(v) => set('status', asList(v))} />
          </Box>
        )}
        <Box sx={{ width: 150 }}>
          <SelectField multiple placeholder="Solution" options={SOLUTION_OPTIONS} value={filters.solution} onChange={(v) => set('solution', asList(v))} />
        </Box>
        <Box sx={{ width: 140 }}>
          <SelectField multiple placeholder="Industry" options={INDUSTRY_OPTIONS} value={filters.industry} onChange={(v) => set('industry', asList(v))} />
        </Box>
        {guestOptions.length > 0 && (
          <Box sx={{ width: 150 }}>
            <SelectField multiple placeholder="Guest" options={guestOptions} value={filters.guest} onChange={(v) => set('guest', asList(v))} />
          </Box>
        )}
      </Stack>

      {/* Table view: this joins the table's own toolbar row, so there is no separate count row
          above an otherwise empty toolbar band. Board view has no toolbar, so it stands alone. */}
      {layout !== 'table' && countAndToggle}

      {error ? (
        <EmptyState title="Could not load episodes" text={(error as Error).message} icon={<ClapperboardIcon strokeWidth={1} size={32} />} />
      ) : layout === 'table' ? (
        <Table
          loading={isLoading}
          rows={rows}
          columns={columns}
          getRowId={(r: VerafiedEpisode) => r.id}
          showSearch={false}
          actionLineLeftComponents={countAndToggle}
          defaultSortModel={[{ field: 'title', sort: 'asc' }]}
          exportFilename="verafied-episodes"
          topOffset={420}
          emptyState={<EmptyState title="No episodes match" text="Clear the search or a filter." icon={<ClapperboardIcon strokeWidth={1} size={32} />} />}
        />
      ) : isLoading ? (
        <KanbanBoardSkeleton lanes={EPISODE_BOARD_LANES.length} trailing />
      ) : (
        <KanbanBoard<VerafiedEpisode, VerafiedEpisodeStatus>
          testId="episodes"
          lanes={EPISODE_BOARD_LANES.map((s) => ({ id: s, label: VERAFIED_EPISODE_STATUS_LABELS[s] }))}
          trailing={{
            id: 'shelved',
            label: VERAFIED_EPISODE_STATUS_LABELS.shelved,
            expanded: showShelved,
            onToggle: () => setShowShelved((v) => !v),
            dropHint: 'Drop to shelve',
          }}
          items={rows}
          getId={(e) => e.id}
          laneOf={(e) => e.status}
          renderCard={(e) => <EpisodeCard episode={e} onOpen={(x) => setEditingId(x.id)} />}
          canDrag={canManage}
          verdict={(e, _from, to) => episodeMoveVerdict(e, to)}
          onDrop={move}
          emptyLabel={() => 'None'}
        />
      )}

      <SuggestionsReviewModal open={reviewing} onClose={closeReview} episodes={episodes} />

      <EpisodeFormModal
        open={adding || !!editing}
        episode={editing}
        canManage={canManage}
        onClose={() => {
          setEditingId(null)
          onAddingDone()
        }}
      />
    </Stack>
  )
}
